'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@/utils/tailwindcss-override';
import { Loader2 } from 'lucide-react';
import Link, { useLinkStatus } from 'next/link';
import type React from 'react';

/**
 * The icon and label of a tab. The tapped tab lights up at once; its icon crossfades to a spinner
 * only when the navigation takes longer than half a second, as it does offline or before the
 * prefetch has arrived, so a fast tap never flashes it. Must sit inside the `Link`.
 */
const NavLinkContent: React.FC<{
  icon: React.ElementType;
  label: string;
  isCurrent: boolean;
  color: string | undefined;
}> = t0 => {
  const $ = _c(32);
  const {
    icon: Icon,
    label,
    isCurrent,
    color
  } = t0;
  const {
    pending
  } = useLinkStatus();
  const isHighlighted = isCurrent || pending;
  const t1 = isHighlighted && color === "red";
  let t2;
  if ($[0] !== isHighlighted || $[1] !== t1) {
    t2 = cn("relative rounded-xl", {
      "bg-green-200": isHighlighted,
      "bg-red-200": t1
    });
    $[0] = isHighlighted;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = color === "red";
  let t4;
  if ($[3] !== isHighlighted || $[4] !== pending || $[5] !== t3) {
    t4 = cn("h-9 w-12 px-3 py-1 text-gray-300 transition-opacity", {
      "text-conveniat-green": isHighlighted,
      "text-cevi-red": t3,
      "opacity-0 delay-500": pending
    });
    $[3] = isHighlighted;
    $[4] = pending;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== Icon || $[8] !== t4) {
    t5 = <Icon className={t4} />;
    $[7] = Icon;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = pending && "opacity-100 delay-500";
  let t7;
  if ($[10] !== t6) {
    t7 = cn("absolute inset-0 flex items-center justify-center opacity-0 transition-opacity", t6);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = color === "red";
  let t9;
  if ($[12] !== t8) {
    t9 = cn("text-conveniat-green animate-spin", {
      "text-cevi-red": t8
    });
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <Loader2 className={t9} />;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t7) {
    t11 = <span className={t7} aria-hidden="true">{t10}</span>;
    $[16] = t10;
    $[17] = t7;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t2 || $[21] !== t5) {
    t12 = <div className={t2}>{t5}{t11}</div>;
    $[19] = t11;
    $[20] = t2;
    $[21] = t5;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = color === "red";
  let t14;
  if ($[23] !== isHighlighted || $[24] !== t13) {
    t14 = cn("mt-1 text-xs font-semibold text-gray-400", {
      "text-conveniat-green font-bold": isHighlighted,
      "text-cevi-red": t13
    });
    $[23] = isHighlighted;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== label || $[27] !== t14) {
    t15 = <span className={t14}>{label}</span>;
    $[26] = label;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t12 || $[30] !== t15) {
    t16 = <>{t12}{t15}</>;
    $[29] = t12;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
};
export const NavLink: React.FC<{
  href: string;
  icon: React.ElementType;
  label: string;
  /** the tab of the page that is shown, announced as the current page */
  isCurrent: boolean;
  color: string | undefined;
  className?: string | undefined;
}> = t0 => {
  const $ = _c(13);
  const {
    href,
    icon,
    label,
    isCurrent,
    color,
    className
  } = t0;
  const t1 = isCurrent ? "page" : undefined;
  let t2;
  if ($[0] !== className || $[1] !== isCurrent) {
    t2 = cn("focus-visible:focus-row relative flex h-full flex-1 flex-col items-center justify-center space-y-1 py-2", {
      "text-blue-600": isCurrent
    }, className);
    $[0] = className;
    $[1] = isCurrent;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== color || $[4] !== icon || $[5] !== isCurrent || $[6] !== label) {
    t3 = <NavLinkContent icon={icon} label={label} isCurrent={isCurrent} color={color} />;
    $[3] = color;
    $[4] = icon;
    $[5] = isCurrent;
    $[6] = label;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== href || $[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = <Link href={href} prefetch={true} aria-current={t1} className={t2}>{t3}</Link>;
    $[8] = href;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};